import { AxiosPromise } from 'axios'
import axios from 'axios'
import client from '@/shared/apis/client'
import {
  PaginatedResponse,
  DrugGroup,
  DrugGroupDetail,
  Drug,
  DrugDetail,
  CreateDrugGroupRequest,
  UpdateDrugGroupRequest,
  SoapTemplateGroup,
  SoapTemplatesUpdateRequest,
  AddDrugsToGroupRequest,
  SuccessResponse,
  GroupDrug,
  KeywordSuggestionResponse,
  AiGenerateSoapTemplatesRequest,
  AiGenerateSoapTemplatesResponse,
  AiGenerationUsageResponse,
  DuplicateDetectionResponse,
} from '@/features/internal/drugs/types/soapTemplate'
import { AdvancedSearchCondition } from '@/shared/types/advancedSearch'

/**
 * 医薬品グループ関連API
 */

// 医薬品グループ一覧を取得
export const getDrugGroups = (
  page?: number,
  search?: string,
  ordering = 'name'
): AxiosPromise<PaginatedResponse<DrugGroup>> => {
  const params = new URLSearchParams()
  if (page) params.append('page', page.toString())
  if (search) params.append('search', search)
  // 常に一貫したソート順を指定
  params.append('ordering', ordering)

  const url = `/api/drug-groups/${
    params.toString() ? `?${params.toString()}` : ''
  }`
  return client.get(url)
}

// 医薬品グループを作成
export const createDrugGroup = (
  data: CreateDrugGroupRequest
): AxiosPromise<DrugGroupDetail> => {
  return client.post('/api/drug-groups/', data)
}

// 医薬品グループの詳細を取得
export const getDrugGroup = (
  groupId: string
): AxiosPromise<DrugGroupDetail> => {
  return client.get(`/api/drug-groups/${groupId}/`)
}

// 医薬品グループを更新
export const updateDrugGroup = (
  groupId: string,
  data: UpdateDrugGroupRequest
): AxiosPromise<DrugGroupDetail> => {
  return client.put(`/api/drug-groups/${groupId}/`, data)
}

// 医薬品グループを削除
export const deleteDrugGroup = (
  groupId: string
): AxiosPromise<SuccessResponse> => {
  return client.delete(`/api/drug-groups/${groupId}/`)
}

// 医薬品グループに所属する医薬品一覧を取得
export const getGroupDrugs = (groupId: string): AxiosPromise<GroupDrug[]> => {
  return client.get(`/api/drug-groups/${groupId}/drugs/`)
}

// 医薬品グループに医薬品を追加
export const addDrugsToGroup = (
  groupId: string,
  data: AddDrugsToGroupRequest
): AxiosPromise<SuccessResponse> => {
  return client.post(`/api/drug-groups/${groupId}/drugs/`, data)
}

// 医薬品グループから医薬品を削除
export const removeDrugFromGroup = (
  groupId: string,
  drugId: string
): AxiosPromise<SuccessResponse> => {
  return client.delete(`/api/drug-groups/${groupId}/drugs/${drugId}/`)
}

// 医薬品グループから複数の医薬品を一括削除
export const bulkDeleteDrugsFromGroup = (
  groupId: string,
  drugIds: string[]
): AxiosPromise<{
  deleted_count: number
  remaining_drugs: GroupDrug[]
}> => {
  return client.delete(`/api/drug-groups/${groupId}/drugs/bulk-delete/`, {
    data: { drug_ids: drugIds },
  })
}

// 医薬品グループに関連するSOAPテンプレートを取得
export const getGroupSoapTemplates = (
  groupId: string
): AxiosPromise<SoapTemplateGroup[]> => {
  return client.get(`/api/drug-groups/${groupId}/soaps/`)
}

// 医薬品グループのSOAPテンプレートを一括更新
export const updateGroupSoapTemplates = (
  groupId: string,
  data: SoapTemplatesUpdateRequest
): AxiosPromise<SoapTemplateGroup[]> => {
  return client.put(`/api/drug-groups/${groupId}/soaps/`, data)
}

/**
 * 医薬品関連API
 */

// 医薬品一覧を取得
export const getDrugs = (
  page?: number,
  search?: string,
  ordering = 'product_name'
): AxiosPromise<PaginatedResponse<Drug>> => {
  const params = new URLSearchParams()
  if (page) params.append('page', page.toString())

  // バックエンドAPIが期待するパラメータ名を使用する
  // 検索キーワードには 'search' を使用する
  if (search) params.append('search', search)
  // 常に一貫したソート順を指定
  // Django REST Frameworkの仕様に従ってorderingパラメータを追加
  params.append('ordering', ordering)

  const url = `/api/drugs${params.toString() ? `?${params.toString()}` : ''}`
  return client.get(url)
}

// 通常検索で全ての医薬品を取得（ページネーションなし）- 新API実装済み
export const getAllNormalDrugs = (
  searchKeyword: string
): AxiosPromise<{ results: Drug[]; count: number }> => {
  return client.post('/api/drugs/all/', {
    search: searchKeyword,
  })
}

// 医薬品の詳細を取得
export const getDrug = (drugId: string): AxiosPromise<DrugDetail> => {
  return client.get(`/api/drugs/${drugId}/`)
}

// 高度検索で医薬品を取得
export const getAdvancedDrugs = (
  conditions: AdvancedSearchCondition[],
  page = 1,
  pageSize = 20
): AxiosPromise<PaginatedResponse<Drug>> => {
  const params = new URLSearchParams()
  params.append('page', page.toString())
  params.append('page_size', pageSize.toString())
  params.append('advanced', 'true')

  // 検索条件をJSONエンコードしてクエリパラメータに追加
  const searchConditions = conditions
    .filter((condition) => condition.value.trim())
    .map((condition) => ({
      type: condition.type,
      field: condition.field,
      value: condition.value.trim(),
      operator: condition.operator,
    }))

  if (searchConditions.length > 0) {
    params.append('conditions', JSON.stringify(searchConditions))
  }

  const url = `/api/drugs/?${params.toString()}`
  return client.get(url)
}

// 高度検索で全ての医薬品を取得（ページネーションなし）
export const getAllAdvancedDrugs = (
  conditions: AdvancedSearchCondition[]
): AxiosPromise<{ results: Drug[]; count: number }> => {
  // 検索条件をフィルタして整形
  const searchConditions = conditions
    .filter((condition) => condition.value.trim())
    .map((condition) => ({
      type: condition.type,
      field: condition.field,
      value: condition.value.trim(),
      operator: condition.operator,
    }))

  return client.post('/api/drugs/advanced/all/', {
    conditions: searchConditions,
  })
}

// 剤形選択肢を取得
export const getDosageFormOptions = (): AxiosPromise<{
  dosage_form_options: Array<{ value: string; label: string; range?: string }>
  description: string
}> => {
  return client.get('/api/drugs/dosage-form-options/')
}

// 医薬品に関連するSOAPテンプレートを取得
export const getDrugSoapTemplates = (
  drugId: string
): AxiosPromise<SoapTemplateGroup[]> => {
  return client.get(`/api/drugs/${drugId}/soaps/`)
}

// 医薬品のSOAPテンプレートを一括更新
export const updateDrugSoapTemplates = (
  drugId: string,
  data: SoapTemplatesUpdateRequest
): AxiosPromise<SoapTemplateGroup[]> => {
  return client.put(`/api/drugs/${drugId}/soaps/`, data)
}

/**
 * キーワード提案API
 */

// SOAPテンプレートグループのキーワード提案を取得
export const suggestKeywords = (
  groupId: string
): AxiosPromise<KeywordSuggestionResponse> => {
  return client.post(
    `/api/soap-template-groups/${groupId}/suggest-keywords/`,
    {}
  )
}

/**
 * AI SOAP Template Generation関連API
 */

// AIでSOAPテンプレートを生成
export const aiGenerateSoapTemplates = async (
  data: AiGenerateSoapTemplatesRequest,
  signal?: AbortSignal
): Promise<AiGenerateSoapTemplatesResponse> => {
  try {
    // Axios 0.21 doesn't support AbortSignal directly, but we can still check it
    if (signal?.aborted) {
      throw new Error('Request was aborted')
    }

    const response = await client.post(
      '/api/drug-groups/ai-generate-templates/',
      data
    )

    // Check if request was aborted after completion
    if (signal?.aborted) {
      throw new Error('Request was aborted')
    }

    return response.data
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 409) {
        // 重複検知エラーは特別扱い
        const duplicateError = {
          type: 'DUPLICATE_DETECTION' as const,
          data: error.response.data as DuplicateDetectionResponse,
        }
        throw duplicateError
      }
    }
    throw error
  }
}

// AI生成機能の利用状況を取得
export const getAiGenerationUsage =
  (): AxiosPromise<AiGenerationUsageResponse> => {
    return client.get('/api/drug-groups/ai-generation-usage/')
  }
